import { useCallback, useEffect, useMemo, useState } from "react";
import { Namespace, useTypedT } from "@racoon/i18n";
import type { ExtraFilterItem } from "../../../features/filter/filter.form";
import type { ConnectProto } from "@racoon/api";
type GovGeoAccess = ConnectProto.GovGeoAccess;

/** Options of the "trucks" filter. Several can be selected; the result is their union. */
const TRUCK_FILTER_OPTIONS = {
  all: "all",
  mvo: "mvo",
  government: "government",
} as const;

interface GovOrganizationQueryArg {
  districtOrganizationIds: (string | number)[];
  serviceProviderIds: (string | number)[];
}

interface UseDashboardFiltersReturn {
  extraFilters: ExtraFilterItem[];
  setExtraFilters: (
    filters: ExtraFilterItem[] | ((prev: ExtraFilterItem[]) => ExtraFilterItem[]),
  ) => void;
  govOrganizationQueryArg: GovOrganizationQueryArg;
  /** True when any truck layer is on. */
  showTrucks: boolean;
  /** MVO trucks (Trucks RPC, scoped by the district/MVO filters). */
  showMvoTrucks: boolean;
  /** Akimat trucks (GovernmentTrucks RPC, never scoped by the district/MVO filters). */
  showGovernmentTrucks: boolean;
  /** Turns all truck layers on ("All trucks") or off. */
  setShowTrucks: (show: boolean) => void;
}

export const useDashboardFilters = (govGeoAccesses: GovGeoAccess[]): UseDashboardFiltersReturn => {
  const t = useTypedT(Namespace.Govdash);
  const localizedFilterLabels = useMemo(
    () => ({
      trucks: t("filters.trucks.label"),
      trucksAll: t("filters.trucks.options.all"),
      trucksMvo: t("filters.trucks.options.mvo"),
      trucksGovernment: t("filters.trucks.options.government"),
      district: t("filters.districts.label"),
      mvo: t("filters.mvo.label"),
    }),
    [t],
  );

  const [extraFilters, setExtraFilters] = useState<ExtraFilterItem[]>([
    {
      key: "trucks",
      label: localizedFilterLabels.trucks,
      options: [
        { value: TRUCK_FILTER_OPTIONS.all, label: localizedFilterLabels.trucksAll },
        { value: TRUCK_FILTER_OPTIONS.mvo, label: localizedFilterLabels.trucksMvo },
        { value: TRUCK_FILTER_OPTIONS.government, label: localizedFilterLabels.trucksGovernment },
      ],
      selectedOptions: [],
    },
    {
      key: "district",
      label: localizedFilterLabels.district,
      options: [],
      selectedOptions: [],
    },
    {
      key: "mvo",
      label: localizedFilterLabels.mvo,
      options: [],
      selectedOptions: [],
    },
    // COMMENTED: special-equipment filter
    // {
    //   key: "special-equipment",
    //   label: t("filters.equipment.label"),
    //   options: [
    //     { value: "tbo", label: t("filters.equipment.options.tbo") },
    //     { value: "so", label: t("filters.equipment.options.so") },
    //     { value: "bunker", label: t("filters.equipment.options.bunker") },
    //   ],
    //   selectedOptions: [],
    // },
    // COMMENTED: waste-generator filter
    // {
    //   key: "waste-generator",
    //   label: t("filters.generators.label"),
    //   options: [
    //     { value: "household", label: t("filters.generators.options.household") },
    //     { value: "construction", label: t("filters.generators.options.construction") },
    //     { value: "commercial", label: t("filters.generators.options.commercial") },
    //   ],
    //   selectedOptions: [],
    // },
  ]);

  // Keep static filter labels in sync with active locale.
  useEffect(() => {
    setExtraFilters((prev) =>
      prev.map((filter) => {
        if (filter.key === "trucks") {
          const optionLabels: Record<string, string> = {
            [TRUCK_FILTER_OPTIONS.all]: localizedFilterLabels.trucksAll,
            [TRUCK_FILTER_OPTIONS.mvo]: localizedFilterLabels.trucksMvo,
            [TRUCK_FILTER_OPTIONS.government]: localizedFilterLabels.trucksGovernment,
          };
          return {
            ...filter,
            label: localizedFilterLabels.trucks,
            options: filter.options.map((option) => ({
              ...option,
              label: optionLabels[option.value] ?? option.label,
            })),
          };
        }
        if (filter.key === "district") {
          return { ...filter, label: localizedFilterLabels.district };
        }
        if (filter.key === "mvo") {
          return { ...filter, label: localizedFilterLabels.mvo };
        }
        return filter;
      }),
    );
  }, [localizedFilterLabels]);

  // Load district options dynamically from GetGovGeoAccess response and set default selection
  // GovGeoAccess contains: districtOrganization (organizationId, areaName) + nested serviceProviders[]
  useEffect(() => {
    if (govGeoAccesses.length > 0) {
      setExtraFilters((prev) => {
        // 1. Build district options from geoAccesses
        // Each entry: { value: organizationId, label: areaName }
        const districtOptions = govGeoAccesses.map((access: GovGeoAccess) => {
          const districtLabel =
            access.areaName || (access as GovGeoAccess & { area_name?: string }).area_name || "";
          return {
            value: (access.organizationId?.toString() || access.organizationId) as string,
            label: districtLabel,
          };
        });

        // 2. Get currently selected districts (no default selection on first load)
        const districtFilter = prev.find((f) => f.key === "district");
        const selectedDistricts = districtFilter?.selectedOptions.length
          ? districtFilter.selectedOptions
          : [];

        // 3. For each selected district (or ALL if none selected), extract its serviceProviders (MVO options)
        // This ensures MVO options are always populated and match the selected districts
        const districtIdsForMvo =
          selectedDistricts.length > 0 ? selectedDistricts : districtOptions.map((d) => d.value);
        const mvoOptionsSet = new Map<string, string>();
        for (const districtId of districtIdsForMvo) {
          const access = govGeoAccesses.find(
            (a: GovGeoAccess) => (a.organizationId?.toString() || a.organizationId) === districtId,
          );
          // Each geoAccess contains serviceProviders array with { id, name }
          if (access) {
            const sps = access.serviceProviders;
            if (sps) {
              for (const sp of sps) {
                const spId = sp.id?.toString() || sp.id;
                mvoOptionsSet.set(spId as string, sp.name);
              }
            }
          }
        }

        const mvoOptions = Array.from(mvoOptionsSet.entries()).map(([value, name]) => ({
          value,
          label: name,
        }));

        return prev.map((filter) => {
          if (filter.key === "district") {
            const newSelectedOptions =
              filter.selectedOptions.length > 0 ? filter.selectedOptions : [];
            return {
              ...filter,
              options: districtOptions,
              selectedOptions: newSelectedOptions,
            };
          }
          if (filter.key === "mvo") {
            return {
              ...filter,
              options: mvoOptions,
            };
          }
          return filter;
        });
      });
    }
  }, [govGeoAccesses]);

  // Update MVO (service provider) options when selected districts change
  // This ensures that when user switches districts, only relevant service providers are shown
  useEffect(() => {
    const districtFilter = extraFilters.find((f) => f.key === "district");
    const selectedDistricts = districtFilter?.selectedOptions || [];

    // If no districts selected, show MVO from all districts; otherwise only from selected districts
    const districtOptionsToUse =
      selectedDistricts.length > 0
        ? selectedDistricts
        : districtFilter?.options.map((d) => d.value) || [];

    // Extract service providers for each selected district from GovGeoAccess.serviceProviders[]
    const mvoOptionsSet = new Map<string, string>();
    for (const districtId of districtOptionsToUse) {
      const access = govGeoAccesses.find(
        (a: GovGeoAccess) => (a.organizationId?.toString() || a.organizationId) === districtId,
      );
      if (access) {
        const sps = access.serviceProviders;
        if (sps) {
          for (const sp of sps) {
            const spId = sp.id?.toString() || sp.id;
            mvoOptionsSet.set(spId as string, sp.name);
          }
        }
      }
    }

    const mvoOptions = Array.from(mvoOptionsSet.entries()).map(([value, name]) => ({
      value,
      label: name,
    }));

    setExtraFilters((prev) =>
      prev.map((filter) => (filter.key === "mvo" ? { ...filter, options: mvoOptions } : filter)),
    );
  }, [
    JSON.stringify(extraFilters.find((f) => f.key === "district")?.selectedOptions),
    JSON.stringify(extraFilters.find((f) => f.key === "district")?.options),
    govGeoAccesses,
  ]);
  const selectedTruckOptions = extraFilters.find((f) => f.key === "trucks")?.selectedOptions;
  const { showMvoTrucks, showGovernmentTrucks } = useMemo(() => {
    const selected = selectedTruckOptions ?? [];
    const all = selected.includes(TRUCK_FILTER_OPTIONS.all);
    return {
      showMvoTrucks: all || selected.includes(TRUCK_FILTER_OPTIONS.mvo),
      showGovernmentTrucks: all || selected.includes(TRUCK_FILTER_OPTIONS.government),
    };
  }, [selectedTruckOptions]);
  const showTrucks = showMvoTrucks || showGovernmentTrucks;

  const setShowTrucks = useCallback((show: boolean) => {
    setExtraFilters((prev) =>
      prev.map((filter) =>
        filter.key === "trucks"
          ? { ...filter, selectedOptions: show ? [TRUCK_FILTER_OPTIONS.all] : [] }
          : filter,
      ),
    );
  }, []);

  // Build query arguments from selected filters
  // These are passed to usePickupLocationsQuery() and useTrucksQuery()
  // to fetch only pickup locations and trucks for selected districts and service providers
  const govOrganizationQueryArg = useMemo(() => {
    const districtFilter = extraFilters.find((f) => f.key === "district");
    const mvoFilter = extraFilters.find((f) => f.key === "mvo");

    // Collect selected district organization IDs
    const districtOrganizationIds = (districtFilter?.selectedOptions ?? []).map((id) =>
      typeof id === "string" ? id : String(id),
    );

    // Collect selected service provider IDs
    const serviceProviderIds = (mvoFilter?.selectedOptions ?? []).map((id) =>
      typeof id === "string" ? id : String(id),
    );

    // Return as strings - will be converted to BigInt in the page component for RPC calls
    return {
      districtOrganizationIds,
      serviceProviderIds,
    };
  }, [extraFilters]);

  return {
    extraFilters,
    setExtraFilters,
    govOrganizationQueryArg,
    showTrucks,
    showMvoTrucks,
    showGovernmentTrucks,
    setShowTrucks,
  };
};
